import { Table } from "@components/ui/Table"
import { formatDate, formatGrade, formatNumber, formatPercent, formatRelative } from "@lib/intl"
import { memo } from "react"
import { useTranslation } from "react-i18next"

import type { ProfessorDetail } from "../types"

type Classroom = ProfessorDetail["classrooms"][number]

/**
 * A professor's classrooms as the latest weekly cut saw them (PRD-56 §FR-12).
 * Figures only — who is in the room belongs to the professor, not the
 * console (ADR-0028). No ranking: the rows follow the classroom's name.
 * The one exception is the "Materiales" tab (ADR-0053 §8): live rows, for
 * superusers, of professors who authorized Platonica — never this page.
 */
export const ProfessorClassrooms = memo(function ProfessorClassrooms({
  professor,
}: {
  professor: ProfessorDetail
}) {
  const { t } = useTranslation("professors")
  const { period, classrooms, grade_scale: scale } = professor
  const dash = <span className="text-ink-subtle">—</span>

  return (
    <section aria-labelledby="classrooms-title" className="space-y-3">
      <div>
        <h2 id="classrooms-title" className="text-ink text-base font-semibold">
          {t("detail.classrooms.title")}
        </h2>
        <p className="text-meta">
          {period
            ? t("detail.classrooms.as_of", { date: formatDate(period, "EEE d MMM") })
            : t("detail.classrooms.no_cut")}
        </p>
      </div>
      {period && classrooms.length === 0 ? (
        <p className="text-ink-muted text-sm">{t("detail.classrooms.empty")}</p>
      ) : (
        classrooms.length > 0 && (
          <Table<Classroom>
            caption={t("detail.classrooms.caption", {
              name: professor.full_name || professor.name,
            })}
            rows={classrooms}
            rowKey={(c) => c.id}
            identity={{
              header: t("detail.classrooms.classroom"),
              cell: (c) => (
                <span className="min-w-0">
                  <span className="text-ink block font-medium">{c.label}</span>
                  <span className="text-meta block">{c.subject}</span>
                </span>
              ),
            }}
            columns={[
              {
                key: "students",
                header: t("detail.classrooms.students"),
                align: "end",
                cell: (c) => formatNumber(c.metrics.students),
              },
              {
                key: "avg",
                header: t("detail.classrooms.average"),
                align: "end",
                cell: (c) =>
                  c.metrics.avg_grade === null ? (
                    <span className="text-ink-subtle whitespace-nowrap">
                      {t("detail.classrooms.no_grades")}
                    </span>
                  ) : (
                    formatGrade(c.metrics.avg_grade, scale)
                  ),
              },
              {
                key: "delivery",
                header: t("detail.classrooms.delivery"),
                align: "end",
                cell: (c) =>
                  c.metrics.delivery_rate === null ? dash : formatPercent(c.metrics.delivery_rate),
              },
              {
                key: "risk",
                header: t("detail.classrooms.at_risk"),
                align: "end",
                cell: (c) => formatNumber(c.metrics.at_risk),
              },
              {
                key: "sent",
                header: t("detail.classrooms.last_sent"),
                cell: (c) =>
                  c.metrics.last_material_sent_at ? (
                    <span className="whitespace-nowrap">
                      {formatRelative(c.metrics.last_material_sent_at)}
                    </span>
                  ) : (
                    <span className="text-ink-subtle">{t("detail.classrooms.never_sent")}</span>
                  ),
              },
            ]}
          />
        )
      )}
    </section>
  )
})
